/**
 * Regional Context
 * Manages user's region and currency (USD/GBP only)
 * Prices are set manually per product - no automatic exchange rates
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

// Supported regions - US and UK only
const REGIONS = {
  US: { code: 'US', name: 'United States', currency: 'USD', symbol: '$', flag: '🇺🇸' },
  GB: { code: 'GB', name: 'United Kingdom', currency: 'GBP', symbol: '£', flag: '🇬🇧' },
};

const RegionalContext = createContext({
  region: REGIONS.US,
  regions: REGIONS,
  setRegion: () => {},
  formatPrice: () => '',
  getProductPrice: () => null,
});

export const RegionalProvider = ({ children }) => {
  const [region, setRegionState] = useState(() => {
    // Initialize from localStorage if available
    const saved = localStorage.getItem('amidex_region');
    // Only allow US or GB
    if (saved === 'US' || saved === 'GB') {
      return REGIONS[saved];
    }
    return REGIONS.US;
  });

  // Set region manually
  const setRegion = useCallback((regionCode) => {
    // Only allow US or GB
    if (regionCode !== 'US' && regionCode !== 'GB') {
      regionCode = 'US';
    }
    const newRegion = REGIONS[regionCode];
    setRegionState(newRegion);
    localStorage.setItem('amidex_region', newRegion.code);
  }, []);

  // Format price with currency symbol
  const formatPrice = useCallback((amount, currency = null) => {
    const curr = currency || region.currency;
    const symbol = curr === 'GBP' ? '£' : '$';
    
    return `${symbol}${Number(amount).toFixed(2)}`;
  }, [region.currency]);

  // Get product price for current region
  // Products have price_usd and price_gbp fields
  const getProductPrice = useCallback((product) => {
    if (!product) return null;
    
    if (region.code === 'GB') {
      // Use GBP price if available, otherwise fall back to USD
      return product.price_gbp || product.price || product.price_usd || 0;
    }
    // Default to USD
    return product.price_usd || product.price || 0;
  }, [region.code]);

  const value = {
    region,
    regions: REGIONS,
    setRegion,
    formatPrice,
    getProductPrice,
    // Keep currentLanguage for any code that might reference it
    currentLanguage: 'en',
  };

  return (
    <RegionalContext.Provider value={value}>
      {children}
    </RegionalContext.Provider>
  );
};

// Hook to use regional context
export const useRegional = () => {
  const context = useContext(RegionalContext);
  if (!context) {
    throw new Error('useRegional must be used within a RegionalProvider');
  }
  return context;
};

export default RegionalContext;
